import React, { useState, useRef, useCallback, useEffect } from 'react';
import AdDisplay from "./AdDisplay";
import { motion, AnimatePresence } from 'framer-motion';
import { Play } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatViews = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
  return num.toString();
};

const VideoCard = ({ video, onClick, index }) => {
  const [hovered, setHovered] = useState(false);
  const cardRef = useRef(null);
  const glowRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const rotateX = (y - 0.5) * -5;
    const rotateY = (x - 0.5) * 5;
    cardRef.current.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-4px)`;
    if (glowRef.current) {
      glowRef.current.style.background = `radial-gradient(circle at ${x*100}% ${y*100}%, rgba(6,182,212,0.25) 0%, transparent 55%)`;
      glowRef.current.style.opacity = '1';
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = 'perspective(800px) rotateX(0deg) rotateY(0deg) translateY(0px)';
    if (glowRef.current) glowRef.current.style.opacity = '0';
    setHovered(false);
  }, []);

  return (
    <motion.div
      className="v-card"
      initial={{ opacity: 0, y: 25 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: index * 0.03, ease: [0.25, 0.46, 0.45, 0.94] }}
      onClick={() => onClick && onClick(video)}
    >
      <div
        ref={cardRef}
        className="v-card-inner"
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={handleMouseLeave}
        style={{ transition: 'transform 0.12s ease-out, box-shadow 0.3s, border-color 0.3s' }}
      >
        <div ref={glowRef} className="v-card-glow" style={{ transition: 'opacity 0.3s' }} />
        <div className="v-thumb-wrap">
          <img src={video.thumbnail} alt={video.title} className="v-thumb" loading="lazy" />
          <span className="v-quality">{video.quality}</span>
          <AnimatePresence>
            {hovered && (
              <motion.div
                className="v-play-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <motion.div
                  className="v-play-circle"
                  initial={{ scale: 0.6 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.6 }}
                  transition={{ duration: 0.2 }}
                >
                  <Play size={22} fill="white" color="white" />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="v-info">
          <h3 className="v-title">{video.title}</h3>
          <div className="v-meta">
            <span className="v-uploader">{video.category || 'Video'}</span>
            <span className="v-sep">-</span>
            <span>{formatViews(video.views)} Views</span>
            <span className="v-sep">-</span>
            <span className="v-duration">{video.duration}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const VideoGrid = ({ onVideoClick, searchQuery, activeTab }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 20;

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const params = { page, limit: perPage };
        if (searchQuery && searchQuery.trim()) {
          params.search = searchQuery.trim();
        }
        if (activeTab && activeTab !== "best") {
          if (activeTab === "terbaru") params.sort = "new";
          else if (activeTab === "trending") params.sort = "trending";
          else if (activeTab !== "cerita") params.category = activeTab;
        }
        const res = await axios.get(`${BACKEND_URL}/api/videos`, { params });
        setVideos(res.data.videos || []);
        setTotalPages(res.data.pages || 1);
      } catch (error) {
        console.error('Error fetching videos:', error);
        setVideos([]);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, [page, searchQuery, activeTab]);

  if (loading) {
    return (
      <div className="video-grid">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="v-card skeleton-card">
            <div className="skeleton-thumb"></div>
            <div className="skeleton-text"></div>
            <div className="skeleton-text short"></div>
          </div>
        ))}
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
        <h3 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>No videos found</h3>
        <p>Try adding some videos from the Admin Panel</p>
      </div>
    );
  }

  return (
    <div>
      <AdDisplay position="top" />
      <div className="grid-header"><h2 className="grid-title">Latest Videos</h2></div>
      <div className="video-grid">
        {videos.map((v, idx) => (
          <VideoCard key={v.id} video={v} onClick={onVideoClick} index={idx} />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination-bar">
          {page > 1 && (
            <button className="pg-btn" onClick={() => setPage(page - 1)}>« Prev</button>
          )}
          {Array.from({ length: Math.min(totalPages, 8) }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              className={`pg-btn ${page === p ? 'active' : ''}`}
              onClick={() => setPage(p)}
            >
              {p}
            </button>
          ))}
          {page < totalPages && (
            <button className="pg-btn" onClick={() => setPage(page + 1)}>Next »</button>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoGrid;
